import Service, { service } from '@ember/service';
import type ImpersonateService from './impersonate';

interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  cacheKey: string;
}

export interface CacheOptions {
  duration?: number; // Cache duration in milliseconds
  useImpersonationKey?: boolean; // Whether to include impersonation in cache key
}

export default class CacheManagerService extends Service {
  @service('impersonate') declare impersonateService: ImpersonateService;

  private cache = new Map<string, CacheEntry>();
  private readonly DEFAULT_CACHE_DURATION = 1 * 60 * 1000; // 1 minutes

  /**
   * Generate a cache key based on provided key and current context
   */
  private generateCacheKey(
    baseKey: string,
    options: CacheOptions = {},
  ): string {
    const parts = [baseKey];

    if (options.useImpersonationKey) {
      const impersonationId = this.impersonateService.getImpersonatingId();
      parts.push(
        impersonationId
          ? `impersonation:${impersonationId}`
          : 'no-impersonation',
      );
    }

    return parts.join('|');
  }

  /**
   * Get cached data if valid, otherwise return null
   */
  getCache<T>(key: string, options: CacheOptions = {}): T | null {
    const cacheKey = this.generateCacheKey(key, options);
    const entry = this.cache.get(cacheKey);

    if (!entry) {
      return null;
    }

    const duration = options.duration ?? this.DEFAULT_CACHE_DURATION;
    const isExpired = Date.now() - entry.timestamp > duration;

    if (isExpired) {
      this.cache.delete(cacheKey);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set cached data
   */
  setCache<T>(key: string, data: T, options: CacheOptions = {}): void {
    const cacheKey = this.generateCacheKey(key, options);
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      cacheKey,
    };

    this.cache.set(cacheKey, entry);
  }

  /**
   * Clear cache entries by pattern or specific key
   */
  clear(keyPattern?: string): void {
    if (!keyPattern) {
      this.cache.clear();
      return;
    }

    const keysToDelete: string[] = [];
    for (const [key] of this.cache) {
      if (key.includes(keyPattern)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:cache-manager')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('cache-manager') declare altName: CacheManagerService;`.
declare module '@ember/service' {
  interface Registry {
    'cache-manager': CacheManagerService;
  }
}
